/**
 * CloneCast design kit.
 *
 * These primitives encode the exact visual language of the Overview and
 * Content Vault pages — spacing, type scale, control sizes, radii, density
 * and status colours. Every other page is built from them so the product
 * reads as one thing. Change it here, not per page.
 */
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

/* ---------- status + mode vocabulary (shared by every page) ---------- */
export const STATUS_COLOR = {
  running: "var(--c-emerald)",
  connected: "var(--c-emerald)",
  active: "var(--c-emerald)",
  completed: "var(--c-sky)",
  idle: "var(--tg-faint)",
  disconnected: "var(--tg-faint)",
  stopped: "var(--c-amber)",
  paused: "var(--c-violet)",
  queued: "var(--tg-muted)",
  connecting: "var(--c-amber)",
  reconnecting: "var(--c-amber)",
  rate_limited: "var(--c-pink)",
  limited: "var(--c-pink)",
  needs_reverification: "var(--c-amber)",
  session_error: "var(--c-rose)",
  frozen: "var(--c-rose)",
  error: "var(--c-rose)",
  failed: "var(--c-rose)",
  critical: "var(--c-rose)",
  warning: "var(--c-amber)",
  info: "var(--c-sky)",
};

export const MODE_COLOR = {
  live: "var(--c-sky)",
  historical: "var(--c-teal)",
  mass_replace: "var(--c-violet)",
  archive: "var(--c-amber)",
  replay: "var(--c-emerald)",
};

export const toneOf = (key) => STATUS_COLOR[key] || "var(--tg-faint)";
export const rgbTone = (cssVar) => cssVar.replace("var(--c-", "rgb(var(--c-").replace(")", "-rgb)/0.15)");

/* ---------- page scaffolding ---------- */
export const PageShell = ({ children, className = "" }) => (
  <div className={`w-full max-w-[1800px] space-y-5 ${className}`}>{children}</div>
);

export const PageHead = ({ title, count, chips, children }) => (
  <div className="flex flex-wrap justify-between items-center gap-3">
    <div className="flex items-center gap-3 flex-wrap min-w-0">
      <div className="label-caps">
        {title}
        {count !== undefined && count !== null ? ` (${count})` : ""}
      </div>
      {chips}
    </div>
    {children ? <div className="flex items-center gap-2 flex-wrap">{children}</div> : null}
  </div>
);

/** Small count chips used beside a page title, e.g. "11 running". */
export const CountChip = ({ tone, label, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full transition-colors ${
      onClick ? "cursor-pointer" : "cursor-default"
    } ${active ? "ring-1" : ""}`}
    style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)`, ringColor: tone }}
  >
    {label}
  </button>
);

/* ---------- stat tiles ---------- */
/** The Overview tile: accent rail, tinted icon chip, one hue per card. */
export const StatTile = ({ label, value, sub, icon: Icon, accent, tint, alert, testid, onClick }) => {
  const ink = alert ? "var(--c-rose)" : accent || "var(--tg-faint)";
  const wash = alert ? "var(--t-rose)" : tint || "rgba(140,147,166,.12)";
  return (
    <Card
      className={`bg-[var(--tg-surface)] p-5 h-full border-l-4 border-[var(--tg-border)] transition-all duration-200 fade-up ${
        onClick ? "cursor-pointer hover:-translate-y-0.5 hover:border-[var(--tg-border-hi)]" : ""
      }`}
      style={{ borderLeftColor: ink }}
      onClick={onClick}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="label-caps">{label}</div>
        <span className="tint-chip h-8 w-8" style={{ background: wash, color: ink }}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div
        className="font-head font-bold text-3xl xl:text-4xl mt-3 tracking-tight tabular-nums truncate"
        style={alert ? { color: ink } : undefined}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-[var(--tg-faint)] mt-1 truncate">{sub}</div>}
    </Card>
  );
};

/** The Vault tile: compact, horizontal, for dense summary strips. */
export const MiniStat = ({ label, value, icon: Icon, accent, tint, testid, onClick, active }) => (
  <Card
    className={`bg-[var(--tg-surface)] border-[var(--tg-border)] border-l-4 p-4 flex items-center gap-3 transition-colors ${
      onClick ? "cursor-pointer hover:border-[var(--tg-border-hi)]" : ""
    } ${active ? "ring-1 ring-inset" : ""}`}
    style={{ borderLeftColor: accent || "var(--tg-faint)", ringColor: accent }}
    onClick={onClick}
    data-testid={testid}
  >
    <span className="tint-chip h-9 w-9" style={{ background: tint || "rgba(140,147,166,.12)", color: accent || "var(--tg-faint)" }}>
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <div className="label-caps">{label}</div>
      <div className="font-head font-bold text-xl tabular-nums truncate">{value}</div>
    </div>
  </Card>
);

export const StatStrip = ({ children, cols = "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" }) => (
  <div className={`grid grid-cols-2 ${cols} gap-4`}>{children}</div>
);

/* ---------- toolbar ---------- */
export const Toolbar = ({ children }) => (
  <div className="flex flex-wrap items-center gap-2">{children}</div>
);

export const SearchInput = ({ value, onChange, placeholder = "Search…", testid, onSubmit }) => {
  const field = (
    <>
      <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tg-faint)] pointer-events-none" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="bg-[var(--tg-bg)] border-[var(--tg-border)] pl-9 h-9 focus:border-[var(--c-sky)]"
      />
    </>
  );
  return onSubmit ? (
    <form className="relative flex-1 min-w-[200px]" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>{field}</form>
  ) : (
    <div className="relative flex-1 min-w-[200px]">{field}</div>
  );
};

export const FilterSelect = ({ value, onValueChange, options, width = "w-36", testid }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className={`${width} h-9 bg-[var(--tg-bg)] border-[var(--tg-border)]`} data-testid={testid}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/* ---------- loading skeletons ----------
   A page must never render its real empty state while its first fetch is
   still in flight — "not loaded yet" and "genuinely empty" are different
   things, and conflating them is what makes navigation flicker. */
export const Skeleton = ({ className = "" }) => (
  <span className={`block rounded bg-[var(--tg-hover)] animate-pulse ${className}`} aria-hidden="true" />
);

export const MiniStatSkeleton = () => (
  <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] border-l-4 p-4 flex items-center gap-3">
    <Skeleton className="h-9 w-9 rounded-[10px]" />
    <div className="min-w-0 flex-1 space-y-1.5">
      <Skeleton className="h-2.5 w-16" />
      <Skeleton className="h-5 w-12" />
    </div>
  </Card>
);

export const StatTileSkeleton = () => (
  <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] border-l-4 p-5 h-full">
    <div className="flex items-start justify-between gap-2">
      <Skeleton className="h-2.5 w-20" />
      <Skeleton className="h-8 w-8 rounded-[10px]" />
    </div>
    <Skeleton className="h-9 w-24 mt-3" />
    <Skeleton className="h-2.5 w-28 mt-2" />
  </Card>
);

/* ---------- table ---------- */
export const DataTable = ({
  columns, children, empty, testid, minWidth = "min-w-[720px]",
  loading = false, skeletonRows = 8,
}) => (
  <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] overflow-hidden" data-testid={testid}>
    <div className="overflow-x-auto max-h-[calc(100vh-320px)]">
      <table className={`w-full text-left ${minWidth}`}>
        <thead className="sticky top-0 z-10 bg-[var(--tg-surface)]">
          <tr className="border-b border-[var(--tg-border)]">
            {columns.map((c) => (
              <th
                key={c.key}
                className={`py-2.5 px-3 label-caps font-medium whitespace-nowrap ${c.align === "right" ? "text-right" : ""} ${c.hide || ""}`}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: skeletonRows }).map((_, r) => (
                <tr key={`sk-${r}`} className="border-b border-[var(--tg-line-soft)]" data-testid="row-skeleton">
                  {columns.map((c) => (
                    <td key={c.key} className={`py-2.5 px-3 ${c.hide || ""}`}>
                      <Skeleton className={`h-3 ${c.align === "right" ? "ml-auto w-10" : "w-full max-w-[140px]"}`} />
                    </td>
                  ))}
                </tr>
              ))
            : children}
        </tbody>
      </table>
    </div>
    {loading ? null : empty}
  </Card>
);

/** Row tone subtly flags attention-worthy records without shouting. */
export const Row = ({ children, tone, testid }) => (
  <tr
    className="border-b border-[var(--tg-line-soft)] hover:bg-[var(--tg-hover)] transition-colors"
    style={tone ? { background: `color-mix(in srgb, ${tone} 7%, transparent)` } : undefined}
    data-testid={testid}
  >
    {children}
  </tr>
);

export const Td = ({ children, align, className = "", title, colSpan }) => (
  <td
    colSpan={colSpan}
    title={title}
    className={`py-2.5 px-3 ${align === "right" ? "text-right" : ""} ${className}`}
  >
    {children}
  </td>
);

/* ---------- atoms ---------- */
export const Pill = ({ tone = "var(--tg-faint)", children, dot, title, testid, size = "md" }) => (
  <span
    title={title}
    data-testid={testid}
    className={`uppercase tracking-wider font-bold rounded-full inline-flex items-center gap-1 whitespace-nowrap ${
      size === "sm" ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5"
    }`}
    style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}
  >
    {dot && <span className="h-1 w-1 rounded-full" style={{ background: tone }} />}
    {children}
  </span>
);

export const IconBtn = ({ icon: Icon, onClick, title, tone, disabled, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    disabled={disabled}
    data-testid={testid}
    className="p-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[var(--tg-hover)]"
    style={{ color: tone || "var(--tg-muted)" }}
  >
    <Icon className="h-3.5 w-3.5" />
  </button>
);

/** Long text stays on one line; the full value is available on hover. */
export const Truncate = ({ children, className = "", max = "max-w-[260px]" }) => (
  <div className={`truncate ${max} ${className}`} title={typeof children === "string" ? children : undefined}>
    {children}
  </div>
);

export const EmptyState = ({ icon: Icon, title, hint, testid, children }) => (
  <div className="p-12 text-center" data-testid={testid}>
    {Icon && <Icon className="h-8 w-8 mx-auto text-[var(--tg-faint)]" />}
    <div className="font-head font-bold text-lg mt-3">{title}</div>
    {hint && <p className="text-[var(--tg-faint)] mt-1.5 text-sm max-w-md mx-auto">{hint}</p>}
    {children && <div className="mt-4">{children}</div>}
  </div>
);

export const Pagination = ({ page, pageCount, total, from, to, onPrev, onNext, pageSize, onPageSize, testid }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div className="flex items-center gap-3">
      <div className="text-xs text-[var(--tg-faint)] tabular-nums" data-testid={testid && `${testid}-info`}>
        {total ? `${from}–${to} of ${total}` : "0 records"}
      </div>
      {onPageSize && (
        <FilterSelect
          value={String(pageSize)}
          onValueChange={(v) => onPageSize(Number(v))}
          width="w-[92px]"
          testid={testid && `${testid}-size`}
          options={[
            { value: "25", label: "25 rows" },
            { value: "50", label: "50 rows" },
            { value: "100", label: "100 rows" },
          ]}
        />
      )}
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={onPrev}
        disabled={page === 0}
        data-testid={testid && `${testid}-prev`}
        className="px-2.5 py-1.5 rounded border border-[var(--tg-border)] text-xs hover:bg-[var(--tg-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="text-xs text-[var(--tg-muted)] tabular-nums">{page + 1} / {pageCount}</span>
      <button
        onClick={onNext}
        disabled={page >= pageCount - 1}
        data-testid={testid && `${testid}-next`}
        className="px-2.5 py-1.5 rounded border border-[var(--tg-border)] text-xs hover:bg-[var(--tg-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);

/* ---------- shared helpers ---------- */
export const relativeTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export const fmtBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

/** Client-side search + filter + sort + paginate for table pages. */
export function usePagedRows(rows, { page, pageSize }) {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const slice = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);
  return {
    slice,
    total,
    pageCount,
    safePage,
    from: total ? safePage * pageSize + 1 : 0,
    to: Math.min((safePage + 1) * pageSize, total),
  };
}
